import { Component, Inject, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, forkJoin } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { LoadingBarService } from '@ngx-loading-bar/core';
import Swal from 'sweetalert2';
import { RewardsHttpService } from '@core/services/rewards-http.service';
import { GameProviderHttpService } from '@core/services/game-provider-http.service';
import { MemberDataService } from '../../../members/services/member-data.service';
import { TransferDataService } from '../../../transfers/services/transfer-data.service';
import { TransferOutConfirmationDialogComponent } from '../transfer-out-confirmation-dialog/transfer-out-confirmation-dialog.component';
import { RestrictionWarningDialogComponent } from '../restriction-warning-dialog/restriction-warning-dialog.component';

interface GameProviderDetails {
  id: number;
  name: string;
  balance: number;
  status: number;
  code?: string;
  promotion_ids?: number[];
  game_provider_id?: number;
  game_provider_name?: string;
  game_provider_code?: string;
  label?: string;
  // Maintenance / suspend fields (optional)
  suspend_transfer_in_status?: number;
  suspend_transfer_in_date?: string | null;
  suspend_transfer_out_status?: number;
  suspend_transfer_out_date?: string | null;
  maintain_from?: string | null;
  maintain_to?: string | null;
}

@Component({
  selector: 'kt-claim-reward-dialog',
  templateUrl: './claim-reward-dialog.component.html',
  styleUrls: ['./claim-reward-dialog.component.scss']
})
export class ClaimRewardDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  loading = false;
  loadingData = true;
  showAdditionalFields = false;
  selectedRewardType = '';
  checkValidation = false;
  subscription = new Subscription();

  // API data
  membersList: any[] = [];
  filteredMembersList: any[] = [];
  isLoadingMembers = false;
  membersSearchInput = '';
  showMembersDropdown = false;
  rewardsDropdown: any[] = [];
  filteredRewardsDropdown: any[] = [];
  rewardSearchInput = '';
  showRewardDropdown = false;
  selectedReward: any;
  selectedMember: any = null;
  rewardsLoading = false;
  
  // Wallet and provider data
  memberBalance: number = 0;
  providerList: GameProviderDetails[] = [];
  filteredTransferFromList: GameProviderDetails[] = [];
  transferFromSearchInput = '';
  showTransferFromDropdown = false;
  gameCategoryList: GameProviderDetails[] = [];
  memberProvidersList: GameProviderDetails[] = [];
  allGameProvidersList: GameProviderDetails[] = [];  // All available game providers
  selectedTransferFrom: GameProviderDetails;
  
  // Transfer To data - filtered based on promotion's game provider
  transferToSearchInput = '';
  showTransferToDropdown = false;
  filteredTransferToList: GameProviderDetails[] = [];
  selectedTransferTo: GameProviderDetails;
  
  // Provider mappings for looking up providers by code or ID
  providerMapByCode: Map<string, any> = new Map();
  
  
  private TARGET_TYPE_NAME_MAP: { [key: string]: number } = {
    'Turnover incl. Transfer Amount': 1,
    'Winloss incl. Transfer Amount': 2,
    'Turnover excl. Transfer Amount': 3,
    'Winloss excl. Transfer Amount': 4,
    'Bonus Round Winning Amount': 5,
    'Turnover': 6,
    'Winover': 7
  };
  
  isLoadingWallets = false;

  // Permission and Bypass Table data
  hasUnrestrictedRewardPermission = false;

  // Settings for kt-member-dropdown
  dropdownSettings: any = {
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'username',
    lazyLoading: true,
    noDataLabel: '',
    showCheckbox: false
  };

  // Settings for rewards dropdown (kt-dropdown-wo-lazyload)
  rewardsDropdownSettings: any = {
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    autoPosition: false,
    classes: 'dropdown',
    maxHeight: '150',
    primaryKey: 'id',
    labelKey: 'name',
    showCheckbox: false
  };

  // Settings for provider dropdowns (transfer from / transfer to)
  providerDropdownSettings: any = {
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    autoPosition: false,
    classes: 'dropdown',
    maxHeight: '200',
    primaryKey: 'id',
    labelKey: 'label',
    showCheckbox: false
  };

  constructor(
    public dialogRef: MatDialogRef<ClaimRewardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private loadingBar: LoadingBarService,
    private rewardsHttpService: RewardsHttpService,
    private gameProviderHttpService: GameProviderHttpService,
    private memberDataService: MemberDataService,
    private transferDataService: TransferDataService,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadingBar.start();
    this.checkUserPermissions();
    this.loadMembers();
    this.initializeForm();
    this.loadingBar.complete();
    this.loadingData = false;
    this.cdr.detectChanges();
  }

  // Handle member selection emitted from kt-member-dropdown
  onSelectedMemberChanged(event: any) {
    if (!event || event.length === 0) {
      this.selectedMember = null;
      this.form.patchValue({ username: null }, { emitEvent: false });
      this.form.get('reward').disable();
      this.rewardsDropdown = [];
      this.filteredRewardsDropdown = [];
      this.cdr.detectChanges();
      return;
    }

    const member = event[0];
    // member from kt-member-dropdown should contain id and username
    this.selectMember(member);
  }

  // Handle reward selection from kt-dropdown-wo-lazyload
  onSelectedRewardChanged(event: any) {
    if (!event || event.length === 0) {
      this.selectedReward = null;
      this.form.patchValue({ reward: null, promotion_id: null }, { emitEvent: false });
      this.rewardsDropdown = [];
      this.filteredRewardsDropdown = [];
      this.cdr.detectChanges();
      return;
    }

    const reward = event[0];
    this.selectReward(reward);
  }

  // Handle transfer-from selection from kt-dropdown-wo-lazyload
  onSelectedTransferFromChanged(event: any) {
    if (!event || event.length === 0) {
      this.selectedTransferFrom = null;
      this.form.patchValue({ transfer_from: null, amount: null }, { emitEvent: false });
      this.cdr.detectChanges();
      return;
    }
    const provider = event[0];
    this.selectTransferFrom(provider);
  }

  // Handle transfer-to selection from kt-dropdown-wo-lazyload
  onSelectedTransferToChanged(event: any) {
    if (!event || event.length === 0) {
      this.selectedTransferTo = null;
      this.form.patchValue({ transfer_to: null }, { emitEvent: false });
      this.cdr.detectChanges();
      return;
    }
    const provider = event[0];
    this.selectTransferTo(provider);
  }

  /**
   * Normalize target_type value to numeric code.
   * Accepts number, numeric-string, or label and returns numeric code (or 0).
   */
  private normalizeTargetType(raw: any): number {
    if (raw === undefined || raw === null) return 0;
    if (typeof raw === 'number') return raw;
    const num = Number(raw);
    if (!isNaN(num)) return num;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (this.TARGET_TYPE_NAME_MAP[trimmed]) return this.TARGET_TYPE_NAME_MAP[trimmed];
      // try to match by including key if case differences
      const foundKey = Object.keys(this.TARGET_TYPE_NAME_MAP).find(k => k.toLowerCase() === trimmed.toLowerCase());
      if (foundKey) return this.TARGET_TYPE_NAME_MAP[foundKey];
    }
    return 0;
  }

  /**
   * Check if current user has 'Claim Member Reward (Unrestricted)' permission
   */
  private checkUserPermissions(): void {
    // Get permission from data passed by parent component
    this.hasUnrestrictedRewardPermission = this.data?.hasUnrestrictedRewardPermission || false;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize form based on reward type
   */
  private initializeForm(): void {
    const baseFormGroup: any = {
      username: ['', Validators.required],
      reward: this.fb.control({ value: '', disabled: true }, Validators.required),
      promotion_id: ['']
    };

    this.form = this.fb.group(baseFormGroup);

  }

  /**
   * Load all members for the dropdown
   */
  private loadMembers(): void {
    this.isLoadingMembers = true;
    const params = `?paginate=true`;
    
    this.subscription.add(
      this.memberDataService.getWithQueryDropdown(params).subscribe(
        (members: any) => {
          const membersList = Array.isArray(members) ? members : (members?.rows || []);
          
          this.membersList = membersList.map(m => ({
            id: m.id,
            username: m.username,
            balance: m.balance,
            currency_id: m.currency_id,
            name: m.username
          }));
          
          this.filteredMembersList = [...this.membersList];
          this.isLoadingMembers = false;
          this.cdr.detectChanges();
        },
        error => {
          this.isLoadingMembers = false;
          this.membersList = [];
          this.filteredMembersList = [];
          this.cdr.detectChanges();
        }
      )
    );
  }

  /**
   * Load rewards for selected member
   */
  loadRewardsForMember(memberId: number) {
    this.rewardsLoading = true;
    const params = `?member_account_id=${memberId}&status=0&paginate=true`;
    
    this.subscription.add(
      this.rewardsHttpService.getPlayerRewards(params).subscribe(
        (rewards: any[]) => {
          const filteredRewards = rewards.filter(r => r.member_id === memberId);
          
          const promotionIds = [...new Set(filteredRewards.map(r => r.promotion_id).filter(id => id))];
          if (promotionIds.length > 0) {
              this.loadPromotionDetailsBatch(promotionIds).subscribe(
                  (promotions: any[]) => {
                      const promotionMap = new Map();
                      promotions.forEach(p => {
                        promotionMap.set(String(p.id), p);
                      });
                    
                      this.rewardsDropdown = filteredRewards.map(r => {
                        const promotion = promotionMap.get(String(r.promotion_id));
                        const promoTypeValue = promotion?.promo_type ?? r.promo_type ?? r.promoType;
                        const promoSubTypeValue = promotion?.promo_sub_type ?? r.promo_sub_type ?? r.promoSubType;
                        const promoType = promoTypeValue !== undefined && promoTypeValue !== null ? Number(promoTypeValue) : undefined;
                        const promoSubType = promoSubTypeValue !== undefined && promoSubTypeValue !== null ? Number(promoSubTypeValue) : undefined;
                      
                        const rewardType = this.getRewardTypeFromPromoType(promoType, promoSubType);
                      
                        const targetProviderId = promotion?.free_spin_game_provider_id ??
                          r.free_spin_game_provider_id ??
                          promotion?.game_provider_id ??
                          r.game_provider_id;
                        const targetProviderCode = promotion?.game_provider ?? r.game_provider ?? r.game_provider_code;
                      
                        let amount = 0;
                        if (promoType === 3) {
                          const creditValue = r.credit ?? r.amount;
                          const creditNumber = this.toNumber(creditValue, 0);
                          amount = creditNumber;
                        } else {
                          const amountValue = r.amount ?? promotion?.amount ?? 0;
                          const amountNumber = this.toNumber(amountValue, 0);
                          amount = amountNumber;
                        }
                      
                        return {
                          id: r.id,
                          name: `${r.promo_code} (${r.id})`,
                          promo_code: r.promo_code,
                          promo_type: promoType,
                          promo_sub_type: promoSubType,
                            target_type: this.normalizeTargetType(promotion?.target_type ?? r.target_type),
                          currency: r.currency_code,
                          credit: r.credit,
                          amount: amount,
                          bonus_rate: r.bonus_rate ?? promotion?.bonus_rate,
                          max_bonus: r.max_bonus ?? promotion?.max_bonus,
                          target_multiplier: r.target_multiplier ?? promotion?.target_multiplier,
                          promotion_id: r.promotion_id,
                          status: r.status,
                          type: rewardType,
                          free_spin_game_provider_id: targetProviderId,
                          game_provider_code: targetProviderCode
                        };
                      });
                      this.filteredRewardsDropdown = [...this.rewardsDropdown];
                this.rewardsLoading = false;
                this.cdr.detectChanges();
              },
              error => {
                this.rewardsLoading = false;
                this.rewardsDropdown = [];
                this.cdr.detectChanges();
              }
            );
          } else {
            this.rewardsDropdown = [];
            this.rewardsLoading = false;
            this.cdr.detectChanges();
          }
        },
        error => {
          this.rewardsLoading = false;
          this.rewardsDropdown = [];
          this.cdr.detectChanges();
        }
      )
    );
  }

  /**
   * Load promotion details in batch
   */
  private loadPromotionDetailsBatch(promotionIds: number[]): any {
    return this.rewardsHttpService.getPromotionDetails(promotionIds);
  }

  /**
   * Load wallet and game providers data
   */
  loadWalletAndProviders(promotionId: number) {
    this.isLoadingWallets = true;
    
    this.subscription.add(
      forkJoin({
        gameAccounts: this.gameProviderHttpService.getGameAccountsOfMember(
          this.selectedMember.username,
          this.selectedMember.currency_id
        ),
        gameProviders: this.gameProviderHttpService.getGameProviders(),
      })
      .pipe(
        finalize(() => {
          this.isLoadingWallets = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        ({ gameAccounts, gameProviders }: any) => {
          const normalizeList = (input: any) =>
            Array.isArray(input) ? input : (input?.rows || input?.data?.rows || input?.data || []);

          const accounts = normalizeList(gameAccounts);
          const providers = normalizeList(gameProviders);

          const providerMapByCode = new Map<string, any>();
          const providerMapById = new Map<number, any>();
          providers.forEach((provider: any) => {
            if (provider?.code) providerMapByCode.set(provider.code, provider);
            if (provider?.id !== undefined && provider?.id !== null) providerMapById.set(provider.id, provider);
          });
          
          this.providerMapByCode = providerMapByCode;

          const accountMapByCode = new Map<string, any>();
          const accountMapById = new Map<number, any>();
          accounts.forEach((account: any) => {
            if (account?.game_provider_code) accountMapByCode.set(account.game_provider_code, account);
            if (account?.game_provider_id !== undefined && account?.game_provider_id !== null) {
              accountMapById.set(account.game_provider_id, account);
            }
          });
          
          this.gameCategoryList = accounts.map((account: any) => {
            const provider = providerMapByCode.get(account.game_provider_code);
            return {
              ...account,
              game_provider_name: provider?.name || account.game_provider_code || 'Unknown',
              game_provider_id: provider?.id || account.game_provider_id || 0
            ,
              label: `${provider?.name || account.game_provider_code || 'Unknown'} (${this.toNumber(account?.balance ?? 0, 0).toFixed(2)})`
            };
          });
          
          this.allGameProvidersList = providers.map((provider: any) => {
            const code = provider?.game_provider_code || provider?.code;
            const providerId = provider?.game_provider_id ?? provider?.id ?? provider?.provider_id ?? 0;
            const accountFromCode = code ? accountMapByCode.get(code) : null;
            const accountFromId = providerId !== undefined && providerId !== null ? accountMapById.get(providerId) : null;
            const account = accountFromCode || accountFromId;
            
            const balanceRaw = account?.balance ?? provider?.balance ?? 0;
            const balance = this.toNumber(balanceRaw, 0);
            
            const name = provider?.name || provider?.game_provider_name || code || 'Unknown';
            const status = provider?.status ?? account?.status ?? 1;
            const suspend_transfer_in_status = provider?.suspend_transfer_in_status ?? account?.suspend_transfer_in_status ?? 0;
            const suspend_transfer_in_date = provider?.suspend_transfer_in_date ?? account?.suspend_transfer_in_date ?? null;
            const suspend_transfer_out_status = provider?.suspend_transfer_out_status ?? account?.suspend_transfer_out_status ?? 0;
            const suspend_transfer_out_date = provider?.suspend_transfer_out_date ?? account?.suspend_transfer_out_date ?? null;
            const maintain_from = provider?.maintain_from ?? account?.maintain_from ?? null;
            const maintain_to = provider?.maintain_to ?? account?.maintain_to ?? null;
            
            return {
              id: providerId,
              status: status,
              name: name,
              balance: balance,
              code: code,
              game_provider_id: providerId,
              game_provider_name: name,
              game_provider_code: code,
              label: `${name} (${balance.toFixed(2)})`,
              suspend_transfer_in_status: suspend_transfer_in_status,
              suspend_transfer_in_date: suspend_transfer_in_date,
              suspend_transfer_out_status: suspend_transfer_out_status,
              suspend_transfer_out_date: suspend_transfer_out_date,
              maintain_from: maintain_from,
              maintain_to: maintain_to
            } as GameProviderDetails;
          });

          this.memberProvidersList = providers.map((provider: any) => {
            const code = provider?.game_provider_code || provider?.code;
            const providerId = provider?.game_provider_id ?? provider?.id ?? provider?.provider_id ?? provider?.game_provider;
            const providerFromMaster = (code && providerMapByCode.get(code)) ||
              (providerId !== undefined && providerId !== null ? providerMapById.get(providerId) : null);

            const id = providerId ?? providerFromMaster?.id ?? 0;
            const accountFromCode = code ? accountMapByCode.get(code) : null;
            const accountFromId = id !== undefined && id !== null ? accountMapById.get(id) : null;
            const account = accountFromCode || accountFromId;

            const balanceRaw = account?.balance ?? provider?.balance ?? 0;
            const balance = this.toNumber(balanceRaw, 0);

            const name = provider?.name || provider?.game_provider_name || providerFromMaster?.name || code || 'Unknown';
            const status = provider?.status ?? account?.status ?? providerFromMaster?.status ?? 1;
            const suspend_transfer_in_status = provider?.suspend_transfer_in_status ?? account?.suspend_transfer_in_status ?? providerFromMaster?.suspend_transfer_in_status ?? 0;
            const suspend_transfer_in_date = provider?.suspend_transfer_in_date ?? account?.suspend_transfer_in_date ?? providerFromMaster?.suspend_transfer_in_date ?? null;
            const suspend_transfer_out_status = provider?.suspend_transfer_out_status ?? account?.suspend_transfer_out_status ?? providerFromMaster?.suspend_transfer_out_status ?? 0;
            const suspend_transfer_out_date = provider?.suspend_transfer_out_date ?? account?.suspend_transfer_out_date ?? providerFromMaster?.suspend_transfer_out_date ?? null;

            return {
              id: id,
              status: status,
              name: name,
              balance: balance,
              code: code,
              label: `${name} (${balance.toFixed(2)})`,
              suspend_transfer_in_status: suspend_transfer_in_status,
              suspend_transfer_in_date: suspend_transfer_in_date,
              suspend_transfer_out_status: suspend_transfer_out_status,
              suspend_transfer_out_date: suspend_transfer_out_date
            } as GameProviderDetails;
          });
          
          const rawBalance = this.selectedMember?.balance;
          this.memberBalance = this.toNumber(rawBalance, 0);
          
          if (this.isBTGChallenge()) {
            const btgProvider = this.gameCategoryList.find(
              (gp: any) => gp.game_provider_code?.toLowerCase() === 'btg'
            );
            if (btgProvider) {
              // Keep track of BTG provider for submission, but don't set it in form
              // since Transfer To field has been removed
            }
          }

          this.updateDropdownOptions();
          
          // Populate transfer to list with matching providers for the selected reward
          if (this.selectedReward) {
            this.refreshTransferToList();
          }
          
          this.cdr.detectChanges();
        },
        error => {
          this.isLoadingWallets = false;
          this.cdr.detectChanges();
        }
      )
    );
  }

  /**
   * Check if this is a BTG Challenge promotion
   */
  private isBTGChallenge(): boolean {
    return this.selectedReward &&
           this.selectedReward.promo_type === 1 &&
           this.selectedReward.promo_code?.toUpperCase().includes('BTG');
  }

  /**
   * Update dropdown options for transfer from
   */
  private updateDropdownOptions(): void {
    const baseList = this.memberProvidersList.length > 0
      ? this.memberProvidersList
      : this.gameCategoryList;

    this.providerList = baseList.map((ga: any) => {
      const balance = this.toNumber(ga.balance, 0);

      const name = ga.game_provider_name || ga.name || ga.game_provider_code || 'Unknown';
      const id = ga.game_provider_id !== undefined ? ga.game_provider_id : (ga.id ?? 0);

      return {
        id: id,
        status: ga.status || 1,
        name: name,
        balance: balance,
        code: ga.game_provider_code || ga.code
        ,
        label: `${name} (${balance.toFixed(2)})`
      };
    });

    const mainWalletIndex = this.providerList.findIndex(p => 
      p.id === 0 || p.code === 'MAIN_WALLET' || p.code === 'main_wallet' || !p.code
    );
    
    if (mainWalletIndex > 0) {
      const mainWallet = this.providerList.splice(mainWalletIndex, 1)[0];
      const mainWalletBalance = this.toNumber(this.memberBalance, 0);
      mainWallet.name = 'Main Wallet';
      mainWallet.balance = mainWalletBalance;
      mainWallet.label = `Main Wallet (${mainWalletBalance.toFixed(2)})`;
      this.providerList.unshift(mainWallet);
    } else if (mainWalletIndex === -1) {
      const mainWalletBalance = this.toNumber(this.memberBalance, 0);
      this.providerList.unshift({
        id: 0,
        status: 1,
        name: 'Main Wallet',
        balance: mainWalletBalance,
        label: `Main Wallet (${mainWalletBalance.toFixed(2)})`,
        code: 'MAIN_WALLET'
      } as GameProviderDetails);
    } else if (mainWalletIndex === 0) {
      const mainWalletBalance = this.toNumber(this.memberBalance, 0);
      this.providerList[0].name = 'Main Wallet';
      this.providerList[0].balance = mainWalletBalance;
      this.providerList[0].label = `Main Wallet (${mainWalletBalance.toFixed(2)})`;
    }
    
    this.filteredTransferFromList = [...this.providerList];
    
    if (this.providerList.length > 0) {
      const defaultProvider = this.providerList[0];
      if (defaultProvider.status === 1) {
        this.selectedTransferFrom = defaultProvider;
        const balance = defaultProvider.balance !== undefined && defaultProvider.balance !== null 
          ? defaultProvider.balance 
          : 0;
        this.transferFromSearchInput = `${defaultProvider.name} (${balance.toFixed(2)})`;
        this.form.patchValue({
          amount: defaultProvider.balance,
          transfer_from: defaultProvider.game_provider_id ?? defaultProvider.id,
        }, { emitEvent: false });
      }
    }
  }

  /**
   * Get reward type from promo_type and promo_sub_type
   */
  getRewardTypeFromPromoType(promoType: number | undefined, promoSubType: number | undefined): string {
    if (promoType === 4) return 'free_spin';
    if (promoType === 2) return 'deposit';
    if (promoType === 3) return 'free_credit';
    if (promoType === 1) return 'manual';
    return 'free_credit';
  }

  /**
   * Handle member search input
   */
  onMembersSearchChange(searchText: string): void {
    this.membersSearchInput = searchText;
    
    if (!searchText.trim()) {
      this.filteredMembersList = [...this.membersList];
    } else {
      const searchLower = searchText.toLowerCase();
      this.filteredMembersList = this.membersList.filter(m => 
        m.username.toLowerCase().includes(searchLower)
      );
    }
    
    this.showMembersDropdown = true;
    this.cdr.detectChanges();
  }

  /**
   * Select a member from the dropdown
   */
  selectMember(member: any): void {
    this.selectedMember = member;
    this.membersSearchInput = member.username;
    this.form.patchValue({ username: member.id }, { emitEvent: false });
    this.form.get('reward').enable();
    this.showMembersDropdown = false;
    
    this.form.patchValue({ reward: '' }, { emitEvent: false });
    this.rewardsDropdown = [];
    this.filteredRewardsDropdown = [];
    this.rewardSearchInput = '';
    this.selectedReward = null;
    this.transferFromSearchInput = '';
    this.selectedTransferFrom = null;
    this.transferToSearchInput = '';
    this.selectedTransferTo = null;
    
    this.loadRewardsForMember(member.id);
    this.checkShowAdditionalFields();
    this.cdr.detectChanges();
  }

  /**
   * Close the members dropdown
   */
  closeMembersDropdown(): void {
    setTimeout(() => {
      this.showMembersDropdown = false;
      this.cdr.detectChanges();
    }, 150);
  }

  /**
   * Handle reward search input
   */
  onRewardSearchChange(searchText: string): void {
    this.rewardSearchInput = searchText;
    
    if (!searchText.trim()) {
      this.filteredRewardsDropdown = [...this.rewardsDropdown];
    } else {
      const searchLower = searchText.toLowerCase();
      this.filteredRewardsDropdown = this.rewardsDropdown.filter(r => 
        r.name?.toLowerCase().includes(searchLower) ||
        r.promo_code?.toLowerCase().includes(searchLower)
      );
    }
    
    this.showRewardDropdown = true;
    this.cdr.detectChanges();
  }

  /**
   * Select a reward from the dropdown
   */
  selectReward(reward: any): void {
    this.selectedReward = reward;
    // debug log removed
    this.selectedRewardType = reward.type;
    this.rewardSearchInput = reward.name;
    this.form.patchValue({ 
      reward: reward.id,
      promotion_id: reward.promotion_id 
    }, { emitEvent: false });
    this.showRewardDropdown = false;
    this.transferFromSearchInput = '';
    this.selectedTransferFrom = null;
    this.transferToSearchInput = '';
    this.selectedTransferTo = null;
    
    this.updateFormValidators();
    // Clear transfer-related controls to avoid stale values when switching reward types
    this.form.patchValue({ transfer_from: null, transfer_to: null, amount: null }, { emitEvent: false });
    
    // Load promotion details and then load wallet/providers
    this.loadPromotionDetailsAndProviders(reward.promotion_id);
    
    this.checkShowAdditionalFields();
    // removed debug logs

    this.cdr.detectChanges();
  }

  /**
   * Load promotion details first, then load wallet and providers
   * This ensures proper sequencing for Transfer To filtering
   */
  private loadPromotionDetailsAndProviders(promotionId: number): void {
    if (!promotionId) {
      this.loadWalletAndProviders(promotionId);
      return;
    }

    this.subscription.add(
      this.rewardsHttpService.getPromotionDetails([promotionId]).subscribe(
        (promotions: any[]) => {
          if (promotions && promotions.length > 0) {
            const promotionDetail = promotions[0];
            
            // Preserve reward-specific fields that shouldn't be overwritten
            const preservedFields = {
              id: this.selectedReward.id,
              amount: this.selectedReward.amount,
              credit: this.selectedReward.credit,
              bonus_rate: this.selectedReward.bonus_rate,
              max_bonus: this.selectedReward.max_bonus,
              target_multiplier: this.selectedReward.target_multiplier,
              target_type: this.selectedReward?.target_type,
              promo_code: this.selectedReward.promo_code,
              name: this.selectedReward.name,
              currency: this.selectedReward.currency,
              status: this.selectedReward.status,
              type: this.selectedReward.type,
              promo_type: this.selectedReward.promo_type,
              promo_sub_type: this.selectedReward.promo_sub_type,
              promotion_id: this.selectedReward.promotion_id,
              free_spin_game_provider_id: this.selectedReward.free_spin_game_provider_id,
              game_provider_code: this.selectedReward.game_provider_code
            };
            
            // Merge promotion details into selectedReward
            this.selectedReward = {
              ...promotionDetail,
              ...preservedFields,
              // Override with promotion detail if reward doesn't have it
              promo_type: preservedFields.promo_type ?? promotionDetail?.promo_type,
              promo_sub_type: preservedFields.promo_sub_type ?? promotionDetail?.promo_sub_type,
              free_spin_game_provider_id: preservedFields.free_spin_game_provider_id ?? promotionDetail?.free_spin_game_provider_id,
              game_provider_code: preservedFields.game_provider_code ?? promotionDetail?.game_provider ?? promotionDetail?.free_spin_game_code,
              bonus_rate: preservedFields.bonus_rate ?? promotionDetail?.bonus_rate,
              max_bonus: preservedFields.max_bonus ?? promotionDetail?.max_bonus,
              target_multiplier: preservedFields.target_multiplier ?? promotionDetail?.target_multiplier,
              // normalize target_type to numeric code
              target_type: this.normalizeTargetType(preservedFields.target_type ?? promotionDetail?.target_type ?? this.selectedReward?.target_type),
              // Additional promotion fields
              limit_transfer_out: promotionDetail?.limit_transfer_out ?? promotionDetail?.min_transfer,
              daily_max: promotionDetail?.daily_max,
              max_per_player: promotionDetail?.max_per_player || promotionDetail?.lifetime_max,
              kyc_basic: promotionDetail?.kyc_basic,
              kyc_advanced: promotionDetail?.kyc_advanced,
              kyc_pro: promotionDetail?.kyc_pro,
              last_deposit: promotionDetail?.last_deposit,
              restrict_claim_round_active: promotionDetail?.restrict_claim_round_active
            };

            this.selectedRewardType = this.getRewardTypeFromPromoType(
              this.selectedReward.promo_type,
              this.selectedReward.promo_sub_type
            );
            this.updateFormValidators();
          }
          
          // After promotion details are loaded, load wallet and providers
          this.loadWalletAndProviders(promotionId);
          this.cdr.detectChanges();
        },
        (error) => {
          // Still load wallet and providers even if promotion details fail
          this.loadWalletAndProviders(promotionId);
        }
      )
    );
  }

  allowNumberOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    // allow numbers (0-9)
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // allow decimal point
    if (charCode === 46) {
      return true;
    }

    event.preventDefault();
    return false;
  }

  formatAmount(event: any) {
    let value = event?.target?.value || '';

    // remove invalid characters
    value = value.replace(/[^0-9.]/g, '');

    // prevent multiple decimals
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts[1];
    }

    // limit to 2 decimal places
    if (parts[1]?.length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }

    // Store numeric value in form control so downstream code receives a number
    const numeric = value === '' ? null : Number(value);
    this.form.controls.amount.setValue(numeric, { emitEvent: false });
  }

  /**
   * Close the reward dropdown
   */
  closeRewardDropdown(): void {
    setTimeout(() => {
      this.showRewardDropdown = false;
      this.cdr.detectChanges();
    }, 150);
  }

  /**
   * Check if additional fields should be shown
   */
  checkShowAdditionalFields() {
    const username = this.form.get('username').value;
    const reward = this.form.get('reward').value;
    const hasUsername = username !== null && username !== '' && username !== undefined;
    const hasReward = reward !== null && reward !== '' && reward !== undefined;
    const hasMemberObj = this.selectedMember !== null && this.selectedMember !== undefined;
    const hasRewardObj = this.selectedReward !== null && this.selectedReward !== undefined;

    if ((hasUsername || hasMemberObj) && (hasReward || hasRewardObj)) {
      this.showAdditionalFields = true;
    } else {
      this.showAdditionalFields = false;
      this.clearAdditionalValidators();
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Update form validators based on reward type
   */
  updateFormValidators() {
    // Ensure form controls exist
    if (!this.form.get('transfer_from')) {
      this.form.addControl('transfer_from', this.fb.control(null));
    }
    
    if (!this.form.get('transfer_to')) {
      this.form.addControl('transfer_to', this.fb.control(null));
    }
    if (!this.form.get('amount')) {
      this.form.addControl('amount', this.fb.control(null));
    }

    // Apply validators based on reward type
    if (this.selectedRewardType === 'free_spin' || this.selectedRewardType === 'deposit') {
      const allowZero = this.selectedRewardType === 'free_spin';
      this.form.get('transfer_from').setValidators([Validators.required]);
      this.form.get('transfer_to').setValidators([Validators.required]);
      this.form.get('amount').setValidators([Validators.required, this.amountValidator(allowZero)]);
    } else if (this.selectedRewardType === 'free_credit') {
      this.form.get('transfer_from').clearValidators();
      this.form.get('transfer_to').setValidators([Validators.required]);
      this.form.get('amount').clearValidators();
    }
    
    this.form.get('transfer_from').updateValueAndValidity();
    this.form.get('transfer_to').updateValueAndValidity();
    this.form.get('amount').updateValueAndValidity();
  }

  /**
   * Clear additional validators
   */
  clearAdditionalValidators() {
    ['transfer_from', 'transfer_to', 'amount'].forEach(field => {
      const control = this.form.get(field);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  /**
   * Amount validator - positive numbers, max 2 decimals
   */
  private amountValidator(allowZero: boolean): ValidatorFn {
    return (control: AbstractControl) => {
      const rawValue = control.value;
      if (rawValue === null || rawValue === undefined || rawValue === '') return null;

      const value = parseFloat(rawValue);
      if (isNaN(value) || (!allowZero && value <= 0) || (allowZero && value < 0)) {
        return { invalidNumber: true };
      }

      if (!/^\d+(\.\d{1,2})?$/.test(rawValue.toString())) {
        return { invalidDecimal: true };
      }

      return null;
    };
  }

  /**
   * Show transfer from field only for free_spin and deposit
   */
  showTransferFrom(): boolean {
    return this.showAdditionalFields && 
           (this.selectedRewardType === 'free_spin' || this.selectedRewardType === 'deposit');
  }

  /**
   * Show transfer amount field only for free_spin and deposit
   */
  showTransferAmount(): boolean {
    return this.showTransferFrom();
  }

  /**
   * Show bonus amount field for free_credit and deposit
   */
  showBonusAmount(): boolean {
      return this.showAdditionalFields && 
        (this.selectedRewardType === 'free_credit' || this.selectedRewardType === 'deposit' || this.selectedRewardType === 'free_spin');
  }

  /**
   * Get bonus amount value
   */
  getBonusAmount(): number {
    if (!this.selectedReward) return 0;
    // For free spin promotions bonus is always 0
    if (this.selectedRewardType === 'free_spin') return 0;

    const isFreeCredit = this.selectedRewardType === 'free_credit';
    if (isFreeCredit) {
      const rawAmount = this.selectedReward.amount;
      const rawCredit = this.selectedReward.credit;
      const amountValue = this.toNumber(rawAmount, 0);
      const creditValue = this.toNumber(rawCredit, 0);
      const amount = amountValue !== 0 ? amountValue : creditValue;
      return this.toNumber(amount, 0);
    }
    
    const transferAmount = Number(this.form.get('amount')?.value || 0);
    return this.onBonusAmount(transferAmount, this.selectedReward);
  }

  getRequirementLabel(): string {
    const targetType = Number(this.selectedReward?.target_type ?? 0);
    return targetType === 2 || targetType === 4 ? 'Winover Requirement' : 'Turnover Requirement';
  }

  getRequirementMultiplicand(): number {
    if (!this.selectedReward) {
      return 0;
    }

    const transferAmount = Number(this.form.get('amount')?.value || 0);
    const bonusAmount = this.getBonusAmount();
    const targetType = Number(this.selectedReward?.target_type ?? 0);
    const isFreeCredit = this.selectedRewardType === 'free_credit';
    const isTargetTypeInclusive = targetType === 1 || targetType === 2;

    const rawAmt = this.selectedReward?.amount;
    const rawCred = this.selectedReward?.credit;
    const amtVal = this.toNumber(rawAmt, 0);
    const credVal = this.toNumber(rawCred, 0);
    const freeCreditBase = amtVal !== 0 ? amtVal : credVal;
    if (isFreeCredit) {
      return Number(Number(freeCreditBase).toFixed(2));
    }

    // For deposit promotions, requirement multiplicand should include transfer amount
    const baseAmount = (this.selectedRewardType === 'deposit') ? transferAmount : (isTargetTypeInclusive ? transferAmount : 0);

    return Number((Number(baseAmount) + Number(bonusAmount)).toFixed(2));
  }

  getRequirementMultiplier(): number {
    const targetType = Number(this.selectedReward?.target_type ?? 0);
    const targetMultiplier = Number(this.selectedReward?.target_multiplier ?? 1);

    if (targetType === 2) {
      return targetMultiplier + 1;
    }

    return targetMultiplier;
  }

  /**
   * Whether the transfer amount should be included in the requirement multiplicand
   */
  includeTransferAmountInRequirement(): boolean {
    // For deposit promotions we always include the transfer amount in the requirement display
    if (this.selectedRewardType === 'deposit') return true;
    const targetType = Number(this.selectedReward?.target_type ?? 0);
    return targetType === 1 || targetType === 2;
  }

  getRequirementTotal(): number {
    const multiplicand = this.getRequirementMultiplicand();
    const multiplier = this.getRequirementMultiplier();

    return Number((multiplicand * multiplier).toFixed(2));
  }

  /**
   * Get the transfer amount value used in template for deposit display
   */
  getTransferAmountValue(): number {
    return Number(this.form.get('amount')?.value || 0);
  }

  /**
   * Calculate bonus amount for deposit
   */
  onBonusAmount(transferAmount: number, reward: any): number {
    let bonusRate = Number(reward?.bonus_rate ?? 0);
    if (bonusRate > 1) {
      bonusRate = bonusRate / 100;
    }
    
    let maxBonus = Number(reward?.max_bonus ?? 0);
    if (maxBonus > 1) {
      maxBonus = maxBonus / 100;
    }

    const calculatedBonus = +(transferAmount * bonusRate).toFixed(2);

    if (maxBonus > 0 && calculatedBonus > maxBonus) {
      return maxBonus;
    }

    return calculatedBonus;
  }

  /**
   * Handle transfer from search input
   */
  onTransferFromSearchChange(searchText: string): void {
    this.transferFromSearchInput = searchText;
    
    if (!searchText.trim()) {
      this.filteredTransferFromList = [...this.providerList];
    } else {
      const searchLower = searchText.toLowerCase();
      this.filteredTransferFromList = this.providerList.filter(p => 
        p.name?.toLowerCase().includes(searchLower) ||
        p.code?.toLowerCase().includes(searchLower)
      );
    }
    
    this.showTransferFromDropdown = true;
    this.cdr.detectChanges();
  }

  /**
   * Select a provider from the transfer from dropdown
   */
  selectTransferFrom(provider: GameProviderDetails): void {
    if (provider.status !== 1) return;
    
    this.selectedTransferFrom = provider;
    const balance = provider.balance !== undefined && provider.balance !== null 
      ? provider.balance 
      : 0;
    this.transferFromSearchInput = `${provider.name} (${balance.toFixed(2)})`;
    this.form.patchValue({
      amount: provider.balance,
      transfer_from: provider.game_provider_id ?? provider.id,
    }, { emitEvent: false });
    this.showTransferFromDropdown = false;
    this.cdr.detectChanges();
  }

  /**
   * Close the transfer from dropdown
   */
  closeTransferFromDropdown(): void {
    setTimeout(() => {
      this.showTransferFromDropdown = false;
      this.cdr.detectChanges();
    }, 150);
  }

  /**
   * Handle transfer to search input
   */
  onTransferToSearchChange(searchText: string): void {
    this.transferToSearchInput = searchText;
    this.filterTransferToList(searchText, true);
  }

  /**
   * Refresh transfer-to list without opening the dropdown
   */
  private refreshTransferToList(): void {
    const searchText = this.transferToSearchInput || '';
    this.filterTransferToList(searchText, false);
  }

  /**
   * Shared helper to compute and set `filteredTransferToList` based on selectedReward,
   * memberProvidersList/allGameProvidersList and optional search text.
   * `openDropdown` controls whether dropdown is opened (true) or just refreshed (false).
   */
  private filterTransferToList(searchText: string, openDropdown: boolean): void {
    // Use all available game providers for transfer to
    let baseList = this.memberProvidersList.length > 0 ? this.memberProvidersList : this.allGameProvidersList;

    // Filter by promotion's target game provider
    if (this.selectedReward) {
      const targetProviderId = this.selectedReward.free_spin_game_provider_id;
      const targetProviderCode = this.selectedReward.game_provider_code || this.selectedReward.game_provider;

      // Parse comma-separated codes into array (normalize to lowercase)
      const targetProviderCodes: string[] = (targetProviderCode && typeof targetProviderCode === 'string')
        ? targetProviderCode.split(',').map((s: string) => s.trim().toLowerCase()).filter((s: string) => !!s)
        : [];

      if (targetProviderId || targetProviderCodes.length > 0) {
        baseList = baseList.filter(gp => {
          const providerId = gp.game_provider_id || gp.id;
          const providerCode = (gp.game_provider_code || gp.code || '').toString().toLowerCase();

          // Coerce both sides to numbers for ID comparison to avoid string/number mismatch
          const matchById = targetProviderId && Number(providerId) === Number(targetProviderId);
          const matchByCode = targetProviderCodes.length > 0 && providerCode && targetProviderCodes.includes(providerCode);

          const matched = matchById || matchByCode;
          return matched;
        });
      }
    }

    // Apply search filter
    if (!searchText || !searchText.trim()) {
      this.filteredTransferToList = baseList;
    } else {
      const searchLower = searchText.toLowerCase();
      this.filteredTransferToList = baseList.filter(gp =>
        (gp.game_provider_name || gp.name || '').toString().toLowerCase().includes(searchLower) ||
        (gp.name || '').toString().toLowerCase().includes(searchLower) ||
        (gp.game_provider_code || gp.code || '').toString().toLowerCase().includes(searchLower)
      );
    }

    this.showTransferToDropdown = !!openDropdown;
    this.cdr.detectChanges();
  }

  /**
   * Select a game provider for transfer to
   */
  selectTransferTo(provider: GameProviderDetails): void {
    if (provider.status !== 1) return;
    
    this.selectedTransferTo = provider;
    const providerName = provider.game_provider_name || provider.name || '';
    const balance = provider.balance !== undefined && provider.balance !== null 
      ? provider.balance 
      : 0;
    this.transferToSearchInput = `${providerName} (${balance.toFixed(2)})`;
    this.form.patchValue({
      transfer_to: provider.game_provider_id || provider.id,
    }, { emitEvent: false });
    this.showTransferToDropdown = false;
    this.cdr.detectChanges();
  }

  /**
   * Close the transfer to dropdown
   */
  closeTransferToDropdown(): void {
    setTimeout(() => {
      this.showTransferToDropdown = false;
      this.cdr.detectChanges();
    }, 150);
  }



  /**
   * Close dialog
   */
  onClose() {
    this.dialogRef.close();
  }

  /**
   * Find provider object by id across known lists
   */
  private findProviderById(id: any): GameProviderDetails | undefined {
    if (id === undefined || id === null) return undefined;
    const idNum = Number(id);
    const lists = [this.allGameProvidersList, this.memberProvidersList, this.providerList, this.filteredTransferToList];
    for (const list of lists) {
      if (!list || !Array.isArray(list)) continue;
      const p = list.find((gp: any) => {
        const pid = gp?.game_provider_id ?? gp?.id;
        return pid !== undefined && pid !== null && Number(pid) === idNum;
      });
      if (p) return p as GameProviderDetails;
    }
    return undefined;
  }

  /**
   * Return true when provider id refers to a provider currently under maintenance (non-1 status)
   */
  private isProviderUnderMaintenanceById(id: any, forTransferIn: boolean | null = null): boolean {
    const p = this.findProviderById(id);
    if (!p) return false;

    // Block if provider is disabled
    if (Number(p.status) === 0) return true;

    const now = new Date();

    // Check transfer-in suspension (suspend flag + optional date)
    if (forTransferIn === null || forTransferIn === true) {
      const inStatus = Number(p.suspend_transfer_in_status ?? 0);
      const inDateRaw = p.suspend_transfer_in_date ?? null;
      if (inStatus === 1) {
        if (!inDateRaw) return true;
        const inDate = new Date(inDateRaw);
        if (!isNaN(inDate.getTime()) && now >= inDate) return true;
      }
    }

    // Check provider-level maintenance window (maintain_from/maintain_to)
    const maintainFromRaw = p.maintain_from ?? null;
    const maintainToRaw = p.maintain_to ?? null;
    if (maintainFromRaw && maintainToRaw) {
      const mFrom = new Date(maintainFromRaw);
      const mTo = new Date(maintainToRaw);
      if (!isNaN(mFrom.getTime()) && !isNaN(mTo.getTime())) {
        if (now >= mFrom && now <= mTo) return true;
      }
    }

    // Check transfer-out suspension
    // Check transfer-out suspension (suspend flag + optional date)
    if (forTransferIn === null || forTransferIn === false) {
      const outStatus = Number(p.suspend_transfer_out_status ?? 0);
      const outDateRaw = p.suspend_transfer_out_date ?? null;
      if (outStatus === 1) {
        if (!outDateRaw) return true;
        const outDate = new Date(outDateRaw);
        if (!isNaN(outDate.getTime()) && now >= outDate) return true;
      }
    }

    return false;
  }

  /**
   * Submit claim reward - Validate first, then claim with transfer API
   */
  public onSubmit(): void {
    this.loading = true;
    const data = this.buildSubmitData();

    // Ensure selected providers are not under maintenance before proceeding.
    const fromId = data.transfer_from ?? this.selectedTransferFrom?.game_provider_id ?? this.selectedTransferFrom?.id;
    const toId = data.transfer_to ?? data.game_provider_id ?? this.selectedTransferTo?.game_provider_id ?? this.selectedTransferTo?.id;

    // If transfer_from is a game provider (non-main wallet) and is under maintenance, block submission
    if (fromId !== undefined && fromId !== null && Number(fromId) !== 0 && this.isProviderUnderMaintenanceById(fromId, false)) {
      const p = this.findProviderById(fromId);
      const name = p?.name || p?.game_provider_name || 'Selected provider';
      this.loading = false;
      this.showSwal('Error!', `${name} is currently under ongoing maintenance and cannot be used.`, 'error');
      return;
    }

    // If transfer_to target provider is under maintenance, block submission
    if (toId !== undefined && toId !== null && this.isProviderUnderMaintenanceById(toId, true)) {
      const p2 = this.findProviderById(toId);
      const name2 = p2?.name || p2?.game_provider_name || 'Target provider';
      this.loading = false;
      this.showSwal('Error!', `${name2} is currently under ongoing maintenance and cannot be used.`, 'error');
      return;
    }


    // Step 1: Validate first
    this.subscription.add(
      this.transferDataService.validate(data).subscribe(
        (response: any) => {
          this.loading = false;

          // Check if validation passed without restrictions
          if (response?.success && !response?.has_restrictions) {
            // No restrictions - still check transfer_to wallet balance first
            // balance > 0 => show Transfer Out prompt
            // balance <= 0 => continue and claim directly
            this.showTransferOutConfirmation(data);
            return;
          }

          // If validation passed BUT has restrictions
          if (response?.success && response?.has_restrictions) {
            const errorType = response?.error_type;
            
            this.showRestrictionWarning(response, data);

            return;
          }

          // If validation failed with error_type
          const errorType = response?.error_type;
          
          if (errorType === 'SYSTEM_ERROR') {
            this.showSystemError(response);
            return;
          }
          
          if (errorType === 'PERMISSION_ERROR') {
            // Permission/restriction error - show as error without Yes/No option
            this.showError(response?.message || 'Player is ineligible to claim reward.');
            return;
          }

          // Generic error
          let errorMessage = response?.message || 'Validation failed';
          if (Array.isArray(errorMessage)) {
            errorMessage = errorMessage.join('<br>');
          }

          this.showSwal('Validation Error', errorMessage, 'error');
        },
        error => {
          this.loading = false;

          if (error?.error?.error_type === 'SYSTEM_ERROR') {
            this.showSystemError(error?.error);
            return;
          }

          let errorMessage = error?.error?.message || error?.message || 'Validation failed';
          if (Array.isArray(errorMessage)) {
            errorMessage = errorMessage.join('<br>');
          }

          this.showSwal('Error!', errorMessage, 'error');
        }
      )
    );
  }

  /**
   * Show error message (no Yes/No option - just Ok)
   */
  private showError(message: any): void {
    this.showSwal('Error!', message, 'error');
  }

  private showSystemError(serverResponse?: any): void {
    // Prefer server-provided message and error_code when available.
    const serverMessage = serverResponse?.message ?? serverResponse?.data ?? serverResponse;
    const errorCode = serverResponse?.error_code ?? serverResponse?.errorCode ?? null;

    const messageHtml = this.formatToHtml(serverMessage || 'Something went wrong with the system, please contact support.');
    const codeHtml = errorCode ? `<br><br><strong>${String(errorCode)}</strong>` : '';

    this.showSwal('Error!', `${messageHtml}${codeHtml}`, 'error');
  }

  private toNumber(value: any, fallback: number = 0): number {
    if (value === undefined || value === null || value === '') return fallback;
    const n = (typeof value === 'string') ? parseFloat(value) : Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  private formatToHtml(input: any): string {
    if (Array.isArray(input)) return input.join('<br>');
    return String(input || '');
  }

  private showSwal(title: string, message: any, icon: 'error' | 'success' | 'info' = 'error', confirmButtonText: string = 'OK'): Promise<any> {
    return Swal.fire({
      title,
      html: this.formatToHtml(message),
      icon,
      confirmButtonColor: '#3085d6',
      confirmButtonText
    });
  }

  /**
   * Show restriction warning dialog when validation has_restrictions = true
   * User can choose to bypass restrictions or cancel
   */
  private showRestrictionWarning(response: any, submitData: any): void {

    // Use the dialog's static method to open with restrictions
    const dialogRef = RestrictionWarningDialogComponent.openFromRestrictions(
      this.matDialog,
      response
    );

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        
        if (result && result.action === 'yes') {
          // User confirmed to bypass restrictions
          submitData = submitData || {};
          submitData.bypass_restrictions = true;
          // If transfer_to wallet has balance > 0, show Transfer Out prompt
          // Else continue flow as confirmed and claim directly
          this.showTransferOutConfirmation(submitData);
        } else {
          // User cancelled - stay in claim reward dialog
          this.cdr.detectChanges();
        }
      })
    );
  }

  /**
   * Execute the actual transfer
   */
  private executeTransfer(data: any): void {
    this.loading = true;

    // Determine provider IDs reliably: prefer explicit payload, fall back to selected items
    const fromId = data?.transfer_from ?? this.selectedTransferFrom?.game_provider_id ?? this.selectedTransferFrom?.id;
    const toId = data?.game_provider_id ?? data?.transfer_to ?? this.selectedTransferTo?.game_provider_id ?? this.selectedTransferTo?.id;

    const isFromGameProvider = fromId !== undefined && fromId !== null && Number(fromId) !== 0;

    // Debug: log computed IDs and payload to help diagnose wrong provider mapping
    console.debug('ClaimReward payload debug', {
      dataPayload: data,
      fromId,
      toId,
      selectedTransferFrom: this.selectedTransferFrom,
      selectedTransferTo: this.selectedTransferTo
    });

    if (isFromGameProvider) {
      const transferOutPayload: any = {
        member_account_id: data.member_account_id,
        game_provider_id: fromId,
        transfer_type: 2, // TRANSFER_OUT
        amount: data.amount,
        promotion_id: 0
      };

      this.subscription.add(
        this.transferDataService.add(transferOutPayload as any, true, true).subscribe(
          (transferOutResponse: any) => {
            if (transferOutResponse?.success) {
              // proceed to claim (transfer in to target provider)
              const claimPayload = { ...data };
              // ensure game_provider_id is target
              if (toId !== undefined && toId !== null) claimPayload.game_provider_id = toId;

              this.subscription.add(
                this.transferDataService.add(claimPayload).subscribe(
                  (response: any) => {
                      this.loading = false;
                      if (response?.success) {
                        this.showSwal('Success!', 'Reward claimed successfully!', 'success').then(() => this.dialogRef.close(response));
                      } else {
                        const msg = (response?.message && Array.isArray(response.message)) ? response.message.join('<br>') : (response?.message || 'Failed to claim reward');
                        this.showSwal('Error!', msg, 'error');
                      }
                    this.cdr.detectChanges();
                  },
                  (error) => {
                    this.loading = false;
                    if (error?.error?.error_type === 'SYSTEM_ERROR') {
                      this.showSystemError(error?.error);
                      this.cdr.detectChanges();
                      return;
                    }
                    const errorMessage = error?.error?.message || error?.message || 'Failed to claim reward';
                    this.showSwal('Error!', errorMessage, 'error');
                    this.cdr.detectChanges();
                  }
                )
              );
            } else {
              this.loading = false;
              const msg = transferOutResponse?.message || 'Failed to transfer out from selected game provider';
              this.showSwal('Transfer Out Failed', msg, 'error');
              this.cdr.detectChanges();
            }
          },
          (error) => {
            this.loading = false;
            const errMsg = error?.error?.message || error?.message || 'Failed to transfer out wallet balance. Please try again.';
            this.showSwal('Transfer Error', errMsg, 'error');
            this.cdr.detectChanges();
          }
        )
      );
    } else {
      // direct claim (transfer in from main wallet)
      this.subscription.add(
        this.transferDataService.add(data, false, true).pipe(
          map(res => res)
        ).subscribe(
          (response: any) => {
              this.loading = false;
              if (response?.success) {
                this.showSwal('Success!', 'Reward claimed successfully!', 'success').then(() => this.dialogRef.close(response));
              } else {
                const msg = (response?.message && Array.isArray(response.message)) ? response.message.join('<br>') : (response?.message || 'Failed to claim reward');
                this.showSwal('Error!', msg, 'error');
              }
            this.cdr.detectChanges();
          },
          error => {
            this.loading = false;

            if (error?.error?.error_type === 'SYSTEM_ERROR') {
              this.showSystemError(error?.error);
              this.cdr.detectChanges();
              return;
            }

            const errorMessage = error?.error?.message || error?.message || 'Failed to claim reward';
            this.showSwal('Error!', errorMessage, 'error');
            this.cdr.detectChanges();
          }
        )
      );
    }
  }

  /**
   * Show transfer out confirmation dialog
   * Called when restrictions are met but transfer_to wallet has balance
   */
  private showTransferOutConfirmation(submitData: any): void {
    const targetProviderId = submitData?.game_provider_id;

    const transferToProvider =
      this.selectedTransferTo ||
      this.memberProvidersList.find((provider) => (provider.game_provider_id || provider.id) === targetProviderId) ||
      this.filteredTransferToList.find((provider) => (provider.game_provider_id || provider.id) === targetProviderId) ||
      this.allGameProvidersList.find((provider) => (provider.game_provider_id || provider.id) === targetProviderId);

    const transferToBalance = Number(transferToProvider?.balance || 0);
    const gameProviderName = transferToProvider?.game_provider_name || transferToProvider?.name || 'Unknown';

    if (transferToBalance <= 0) {
      // No balance to transfer out, continue flow as confirmed and claim directly
      this.executeTransfer(submitData);
      return;
    }

    const dialogData = {
      gameProviderName: gameProviderName,
      gameWalletBalance: transferToBalance,
      transferOutData: {
        ...submitData,
        game_wallet_balance: transferToBalance
      }
    };

    const dialogRef = this.matDialog.open(
      TransferOutConfirmationDialogComponent,
      {
        width: '500px',
        disableClose: false,
        data: dialogData
      }
    );

    this.subscription.add(
      dialogRef.afterClosed().subscribe(
        (result) => {
          if (result && result.action === 'success') {
            // Transfer and claim succeeded - close the main reward claim dialog
            this.loading = false;
            this.dialogRef.close(result.data);
          } else if (result && result.action === 'cancel') {
            // User cancelled the transfer out dialog - stay at restrictions warning
            this.loading = false;
            this.cdr.detectChanges();
          }
        }
      )
    );
  }

  /**
   * Build submit data from form
   */
  private buildSubmitData(): any {
    const formValue = this.form.value;
    
    const data: any = {
      member_account_id: this.selectedMember.id,
      transfer_type: 1, // TRANSFER_IN
      promotion_id: this.selectedReward.promotion_id
    };

    const rewardId = this.selectedReward?.id ?? formValue.reward;
    if (rewardId !== undefined && rewardId !== null) {
      data.reward_id = rewardId;
    }

    // Get game provider ID from promotion
    let gameProviderId = this.selectedReward?.free_spin_game_provider_id || this.selectedReward?.game_provider_id;
    if (!gameProviderId && this.selectedReward?.game_provider_code) {
      const provider = this.providerMapByCode.get(this.selectedReward.game_provider_code);
      if (provider?.id) {
        gameProviderId = provider.id;
      }
    }
    
    // For transfer API
    if (!gameProviderId && formValue.transfer_to) {
      gameProviderId = formValue.transfer_to;
    }
    
    if (gameProviderId !== undefined && gameProviderId !== null) {
      data.game_provider_id = gameProviderId;
    }

    // include explicit transfer_from / transfer_to if user selected them
    if (formValue.transfer_from !== undefined && formValue.transfer_from !== null) {
      data.transfer_from = formValue.transfer_from;
    }

    if (formValue.transfer_to !== undefined && formValue.transfer_to !== null) {
      data.transfer_to = formValue.transfer_to;
    }

    if (this.selectedRewardType === 'free_spin' || this.selectedRewardType === 'deposit') {
      data.amount = formValue.amount;
    } else if (this.selectedRewardType === 'free_credit') {
      // Use 'credit' field which stores the reward amount
      data.amount = this.selectedReward.credit || this.selectedReward.amount || 0;
    }

    return data;
  }
}
